/**
 * Reducer + initial-state builder for the AIChatShell slice.
 *
 * The composition root (AIChatProvider) resolves persistedUI + isNewDay ONCE and passes the
 * already-computed isChatbotOpen/chatbotWidth/isDevMode as overrides; this builder only supplies
 * the safe fallback defaults for callers (e.g. tests) that seed a partial slice.
 */
import type { ShellAction, ShellState } from './aiChatShellTypes';

export const AI_CHAT_DEFAULT_WIDTH = 400;
export const AI_CHAT_MIN_WIDTH = 400;
export const AI_CHAT_MAX_WIDTH_PERCENTAGE = 0.5;

export const SHELL_ACTIONS = {
  OPEN_CHATBOT: 'OPEN_CHATBOT',
  CLOSE_CHATBOT: 'CLOSE_CHATBOT',
  TOGGLE_CHATBOT: 'TOGGLE_CHATBOT',
  SET_CHATBOT_WIDTH: 'SET_CHATBOT_WIDTH',
} as const;

/**
 * Hostname-derived dev-mode flag — identical to the check that lived in getInitialChatbotState.
 */
export function detectIsDevMode(): boolean {
  return (
    window.location.hostname === 'localhost' ||
    (window.location.hostname.includes('tenant-ai') && !window.location.hostname.includes('tenant-ai-2.oneclicklabs.es'))
  );
}

/**
 * Build the initial Shell state, applying any provided overrides on top of the documented
 * defaults. Overrides come from the Shell slice of AIChatProvider's own initialState prop.
 */
export function getInitialShellState(overrides: Partial<ShellState> = {}): ShellState {
  return {
    isChatbotOpen: false,
    chatbotWidth: AI_CHAT_DEFAULT_WIDTH,
    isDevMode: detectIsDevMode(),
    ...overrides,
  };
}

export function shellReducer(state: ShellState, action: ShellAction): ShellState {
  switch (action.type) {
    case SHELL_ACTIONS.OPEN_CHATBOT:
      return { ...state, isChatbotOpen: true };
    case SHELL_ACTIONS.CLOSE_CHATBOT:
      return { ...state, isChatbotOpen: false };
    case SHELL_ACTIONS.TOGGLE_CHATBOT:
      return { ...state, isChatbotOpen: !state.isChatbotOpen };
    case SHELL_ACTIONS.SET_CHATBOT_WIDTH:
      return { ...state, chatbotWidth: action.payload };
    default:
      return state;
  }
}
